import { parse as parseCookie, serialize } from "cookie";

const API_BASE_URL = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/$/, "");
const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const DEFAULT_METADATA = {
    title: "Secret Food Tours",
    description: "Secret Food Tours - We are local people, who have a deep passion for our food, history and culture. We would be delighted to show you the real tastes of the world.",
};

const SSR_ENDPOINTS = {
    getAllSlugs: {
        path: () => "/data/slugs",
    },
    getAllSubSlugs: {
        path: () => "/data/subslugs",
    },
    getCommonWorldTourData: {
        path: () => "/data/v2/common/world",
    },
    getCommonData: {
        path: () => "/data/v2/common",
        query: (params = {}) => (params.variant ? { variant: params.variant } : {}),
    },
    getHomepageData: {
        path: () => "/data/v2/homepage",
        commonType: "default",
    },
    getWorldTourHomepageData: {
        path: () => "/data/v2/world_tours_homepage",
        commonType: "world",
    },
    getTermsData: {
        path: () => "/data/v2/terms",
        commonType: "default",
    },
    getTermsUsaData: {
        path: () => "/data/v2/terms_usa",
        commonType: "default",
    },
    getPrivateToursData: {
        path: () => "/data/v2/private_tours",
        commonType: "default",
    },
    getPrivateToursFormData: {
        path: () => "/data/v2/private_tours_form",
        commonType: "default",
    },
    getPrivacyData: {
        path: () => "/data/v2/privacy",
        commonType: "default",
    },
    getJoinUsData: {
        path: () => "/data/v2/join_us",
        commonType: "default",
    },
    getGiftCardsData: {
        path: () => "/data/v2/gift_cards",
        commonType: "default",
    },
    getContactData: {
        path: () => "/data/v2/Contact",
        commonType: "default",
    },
    getBloggersData: {
        path: () => "/data/v2/bloggers",
        commonType: "default",
    },
    getAboutUsData: {
        path: () => "/data/v2/about_us",
        commonType: "default",
    },
    getNewToursData: {
        path: () => "/data/v2/new_tours",
        commonType: "default",
    },
    getReasonsToBookAPrivateTourData: {
        path: () => "/data/v2/reasons_to_book_a_private_tour",
        commonType: "default",
    },
    getFaqData: {
        path: () => "/data/v2/faq",
        commonType: "default",
    },
    getHealthAndSafetyData: {
        path: () => "/data/v2/health_and_safety",
        commonType: "default",
    },
    getWaiverUkData: {
        path: () => "/data/v2/waiver_uk",
        commonType: "default",
    },
    getWaiverUsaData: {
        path: () => "/data/v2/waiver_usa",
        commonType: "default",
    },
    getMediaData: {
        path: () => "/data/v2/media",
        commonType: "default",
    },
    getDataBySlug: {
        path: (params = {}) => `/data/v2/slug/${encodePathSegments(params.slug)}`,
    },
};

const getEndpointConfig = (endpoint) => {
    const config = SSR_ENDPOINTS[endpoint];

    if (!config) {
        throw new Error(`Unknown SSR endpoint "${endpoint}"`);
    }

    return config;
};

const encodePathSegments = (value = "") =>
    String(value)
        .split("/")
        .filter(Boolean)
        .map((segment) => encodeURIComponent(segment))
        .join("/");

const buildEndpointUrl = (endpoint, params = {}) => {
    const config = getEndpointConfig(endpoint);
    const url = new URL(`${API_BASE_URL}${config.path(params)}`);
    const query = config.query?.(params) || {};

    Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            url.searchParams.set(key, value);
        }
    });

    return url.toString();
};

const fetchApiJson = async (endpoint, params = {}) => {
    const response = await fetch(buildEndpointUrl(endpoint, params), {
        method: "GET",
        headers: {
            Accept: "application/json",
            Locale: "en",
            Timezone: DEFAULT_TIMEZONE,
            "App-Device": "web",
        },
    });

    const payload = await response.json();

    if (!response.ok) {
        const error = new Error(payload?.message || `Request failed for ${endpoint}`);
        error.status = response.status;
        throw error;
    }

    return payload;
};

// --- A/B helpers ---
const getVariantFromCookie = (context) => {
    let incomingVariant;
    const cookieHeader = context?.req?.headers?.cookie;
    if (cookieHeader) {
        const parsed = parseCookie(cookieHeader);
        incomingVariant = parsed?.ab_variant;
    }

    // If no cookie, check URL query for variant
    if (!incomingVariant && context?.req?.url) {
        try {
            const url = new URL(context.req.url, `http://${context.req.headers.host}`);
            const queryVariant = url.searchParams.get("variant");
            if (queryVariant) {
                incomingVariant = queryVariant;
            }
        } catch (e) {
            // ignore URL parse errors
        }
    }

    return incomingVariant ?? undefined;
};

const setVariantCookie = (context, variant) => {
    const maxAge = 60 * 60 * 24 * 90; // 90 days
    if (context?.res && variant) {
        const cookie = serialize("ab_variant", variant, {
            httpOnly: true,
            maxAge,
            path: "/",
        });
        const currentCookieHeader = context.res.getHeader("Set-Cookie");

        if (!currentCookieHeader) {
            context.res.setHeader("Set-Cookie", cookie);
            return;
        }

        const cookies = Array.isArray(currentCookieHeader)
            ? currentCookieHeader
            : [currentCookieHeader];

        context.res.setHeader("Set-Cookie", [...cookies, cookie]);
    }
};

const fetchCommonData = async (commonType, context) => {
    const incomingVariant = getVariantFromCookie(context);
    const endpoint = commonType === "world" ? "getCommonWorldTourData" : "getCommonData";
    const response = await fetchApiJson(endpoint, incomingVariant ? { variant: incomingVariant } : {});
    const variant = response?.data?.ab?.variant;

    if (!incomingVariant) {
        setVariantCookie(context, variant);
    }

    return {
        response,
        variant,
    };
};

const buildMergedPageData = (pageResponse, commonResponse, variant) => ({
    data: {
        ...(commonResponse?.data || {}),
        ...(pageResponse?.data || {}),
    },
    variant,
});

const buildMetadata = (pageResponse, defaults = DEFAULT_METADATA) => ({
    title: pageResponse?.meta?.title ?? defaults.title,
    description: pageResponse?.meta?.description ?? defaults.description,
    image: pageResponse?.meta?.image?.url ?? null,
    createdAt: pageResponse?.meta?.createdAt ?? null,
    updatedAt: pageResponse?.meta?.updatedAt ?? null,
});

const trimSlashes = (value = "") => String(value).replace(/^\/+|\/+$/g, "").normalize("NFC");

// The CMS finds a slug whatever its case, so /Raleigh/ rendered the same page as /raleigh/
// and search engines saw two URLs. A URL that differs from the stored slug only in case is
// sent to the stored one, so each page has a single address. Stored slugs are left as they
// are - some have capitals (washington-DC) and those are the URLs already indexed.
const buildCaseRedirect = (requestedSlug, page, context) => {
    const stored = trimSlashes(page?.slug);
    const requested = trimSlashes(requestedSlug);

    if (!stored || stored === requested || stored.toLowerCase() !== requested.toLowerCase()) {
        return null;
    }

    const resolvedUrl = context?.resolvedUrl || "";
    const query = resolvedUrl.includes("?") ? resolvedUrl.slice(resolvedUrl.indexOf("?")) : "";

    return {
        redirect: {
            destination: `/${encodePathSegments(stored)}/${query}`,
            statusCode: 301,
        },
    };
};

const detectIsMobile = (userAgent = "") => /mobile|android|iphone|ipad|phone/i.test(userAgent);
const getIsMobileFromContext = (context) => {
    const userAgent = context?.req?.headers?.["user-agent"] || "";
    return detectIsMobile(userAgent);
};

export const fetchCommonLayoutProps = async (context, options = {}) => {
    if (options.statusCode && context?.res) {
        context.res.statusCode = options.statusCode;
    }

    const commonData = await fetchCommonData(options.commonType || "default", context);

    return {
        props: {
            data: buildMergedPageData({ data: {} }, commonData.response, commonData.variant),
            isMobile: getIsMobileFromContext(context),
        },
    };
};

export const fetchPageData = async (endpoint, context) => {
    const isMobile = getIsMobileFromContext(context);
    const commonType = getEndpointConfig(endpoint).commonType || "default";
    const [pageResponse, commonData] = await Promise.all([
        fetchApiJson(endpoint),
        fetchCommonData(commonType, context),
    ]);

    const mergedData = buildMergedPageData(pageResponse, commonData.response, commonData.variant);

    return {
        props: {
            data: mergedData || null,
            metadata: buildMetadata(pageResponse),
            isMobile,
        },
    };
};

export const fetchStaticProps = async (endpoint, params, context) => {
    const isMobile = getIsMobileFromContext(context);

    try {
        const pageResponse = await fetchApiJson(endpoint, params);
        const pageData = pageResponse?.data;

        if (!pageData?.page?.type) {
            return await fetchCommonLayoutProps(context, { statusCode: 404 });
        }

        if (endpoint === "getDataBySlug") {
            const caseRedirect = buildCaseRedirect(params?.slug, pageData.page, context);

            if (caseRedirect) {
                return caseRedirect;
            }
        }

        const commonData = await fetchCommonData(pageData.isWorldTour ? "world" : "default", context);
        const mergedData = buildMergedPageData(pageResponse, commonData.response, commonData.variant);

        return {
            props: {
                data: mergedData || null,
                metadata: buildMetadata(pageResponse, {
                    title: "Default Title",
                    description: "Default Description",
                }),
                structuredData: pageData?.page?.structuredData ?? null,
                isMobile,
            },
        };
    } catch (error) {
        if (error?.status === 404) {
            return await fetchCommonLayoutProps(context, { statusCode: 404 });
        }

        return {
            props: {
                error: "An unexpected error occurred.",
            },
        };
    }
};

export const fetchStaticPaths = async (endpoint, mapCallback) => {

    const response = await fetchApiJson(endpoint);
    const paths = response?.data?.map(mapCallback) || [];

    return {
        paths,
        fallback: "blocking",
    };
};
